import { Component, OnInit, Input, ElementRef, ViewChild, OnChanges, Output, EventEmitter } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ProjectElement } from '../project-element.interface';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ElementService } from '../element.service';
import { ModalService } from '../../../services/modal/modal.service';
import { TranslateService } from '../../../utilities/translate/translate.service';
import { ConstantsService } from '../../../utilities/constants/constants.service';
import { ElementValuesAction } from './element-values-action.interface';
import { ProjectElementService } from '../../../utilities/stores/project-element/project-element.service';

/**
 * Autocomplete.
 *
 * Supports both a list of options from the elements options array, and
 * manual input by text input.
 */
@Component({
  selector: 'sg2-element-value',
  templateUrl: './element-value.component.html',
  styleUrls: ['./element-value.component.less']
})
export class ElementValueComponent implements  OnChanges, OnInit {

  /** Active element. */
  @Input() element: ProjectElement;

  /** In case we need to add some additional options to the submit() */
  @Input() customValues?: any;

  /** Input field, used to blur after selection. */
  @ViewChild('input') input: ElementRef;

  /** Paragraph input field, used to blur after selection. */
  @ViewChild('inputParagraph') inputParagraph: ElementRef;

  /** Autocomplete. */
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  /** Action output is needed to support legacy code : https://angular.io/api/core/EventEmitter */
  @Output() action = new EventEmitter<ElementValuesAction>();

  /** Form. */
  public form: FormControl;

  /** Lazy loading the component. */
  public lazy = true;

  /** Keeps track of current value and optional key. */
  private option = {
    key: null,
    value: null
  };

  constructor(
    private constantsService: ConstantsService,
    private elementService: ElementService,
    private modalService: ModalService,
    private translateService: TranslateService,
  ) { }

  ngOnInit() {
    this.form = new FormControl(this.element.value, { updateOn: 'blur' });
    this._onFormChange();
  }

  ngOnChanges() {
    this._setOptions();
  }

  /**
   * Add option.
   *
   * Triggers a modal with the option to enter a new option. When entered, the new value is
   * set in the form, and the new option is added to the element so it'll be saved in the list.
   *
   * When no option is entered, the value is restored to the last know value.
   *
   * @todo Use option2 settings.
   */
  public addOption(): void {
    this.modalService.prompt(this.translateService.get('addoption')).afterClosed().subscribe(value => {
      if (value) {
        this.element.options.push({ prefix: this.translateService.get('option'), value: value });
        this.element.selectOptions.push({ active: true, name: value, id: this.element.selectOptions.length });
        this.option = { key: `${ this.translateService.get('option') }:`, value: value };
        this.form.setValue(value);
      } else {
        this.form.setValue(this.element.value, { emitEvent: false });
      }
    });
  }

  /**
   * Edit paragraph text in modal.
   *
   * Triggers on both click and focus (when selecting with tab). Immediately blurs the input field
   * to prevent inputting text, and opens a modal instead.
   */
  public editParagraph(): void {
    this.inputParagraph.nativeElement.blur();
    this.modalService.openTinymce(this.element).afterClosed().subscribe(value => {
      if (value && value !== this.element.value) {
        this.elementService.setValue(this.element, value);
      }
    });
  }

  /**
   * Switch the lazy component for the *real* component.
   */
  public load(): void {
    this.lazy = false;
  }

  /**
   * Selecting an autocomplete option.
   *
   * When selecting an option, both the value and the key are extracted from the event. The
   * key is then set to a temporary variable, so it's accessible on submit.
   *
   * To trigger the form observable, the field is blurred after selection.
   */
  public optionSelected(event: MatAutocompleteSelectedEvent): void {
    const key = event.option.viewValue.substr(0, event.option.viewValue.lastIndexOf(event.option.value) - 2);
    this.option = {
      key: key,
      value: event.option.value
    };
    this.input.nativeElement.blur();
  }

  /**
   * Submit value on enter.
   *
   * - Blur current input and capture current value.
   * - Close autocomplete panel.
   */
  public submitOnEnter(): void {
    this.input.nativeElement.blur();
    this.autocomplete.closePanel();
  }

  /**
   * Track form field.
   *
   * After value change (both from text input and autocomplete), the value is send to the
   * submit() method. When a key (from autocomplete) is available, it's also send to submit().
   * When done, clear the key from memory, so the next time a new value is entered by
   * manual input, there is no key available.
   *
   * For input type 'Select' it's possible to add custom options directly from the dropdown.
   */
  private _onFormChange(): void {
    this.form.valueChanges.pipe().subscribe(change => {
      if (change === 'addOption()') {
        this.form.setValue(null, { emitEvent: false });
        this.addOption();
        return false;
      }

      this.option.value = change;
      this._submit(this.option.value, this.option.key);
      this.option = { key: null, value: null };
    });
  }

  /**
   * Generate options list for element.
   *
   * @note I think this is only needed for the autocomplete, so it can be generated
   * once needed. If not, move to ElementService or Project pipeline.
   *
   * @todo Generate on runtime, for lazy loading the options list.
   */
  private _setOptions(): void {
    const options = [];

    // Basic area value.
    if (this.elementService.isOfType(this.element, 'basicAreaCopy')) {
      options.push({
        label: this.translateService.get('areatemplatevalue'),
        value: this.element.basicAreaValue
      });
    }

    // Template values.
    if (this.element.templateValues) {
      this.element.templateValues.forEach(templateValue => {
        const qualityLevel = this.constantsService.getQualityLevels().find(q => q.value === templateValue.quality);
        options.push({
          label: this.translateService.get('templatevalue') + (qualityLevel
            ? ` (${ qualityLevel.displayName })`
            : ` (${ this.translateService.get('projectsourcevalue') })`),
          value: templateValue.value
        });
      });
    }

    // Custom values.
    if (this.element.selectOptions) {
      this.element.selectOptions.forEach(selectOptions => {
        options.push({
          label: this.translateService.get('option'),
          value: selectOptions.name
        });
      });
    }

    this.element.options2 = options;
  }

  /**
   * Submit value.
   *
   * @param value The new element value.
   * @param [key=null] Optional key when selecting from options/list.
   */
  private _submit(value: string, key: string = null): void {
    this.elementService.patch(this.element, {
      value: value,
      valueKey: key,
      selectOptions: this.element.selectOptions
    }, {
      customValues: this.customValues as Partial<ProjectElement>
    }).subscribe((response) => {
      this.action.emit({ action: 'edit', element: response });
    });
  }
}
